/**
 * @file: LeadCenterContext.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Cross-screen bus for the leads flow. A screen that mutates a lead (the detail
 * page's owner / intention / customer edits, transitions, reservations) emits
 * the updated row on `leadUpdated$`; sibling screens (the list) subscribe to
 * patch their copy in place. This is deliberately decoupled from navigation
 * params — passing a callback through `navigate(...)` breaks deep-linking (a
 * cold deep-link into a sub-screen has no parent to supply the callback), and
 * isn't serializable. A context-scoped subject sidesteps both.
 */

import { Lead } from '@euler/model/lead/Lead';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { ReactNode, createContext, memo, useContext, useMemo } from 'react';
import { Subject } from 'rxjs';

export type LeadCenterContextValue = {
  // A lead's fields changed; carries the full updated row.
  leadUpdated$: Subject<Lead>;
  emitLeadUpdated: (lead: Lead) => void;
};

const LeadCenterContext = createContext<LeadCenterContextValue>(null as any);

export const useLeadCenter = () => useContext(LeadCenterContext);

export const LeadCenterProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const leadUpdated$ = useCreation(() => new Subject<Lead>(), []);
    const emitLeadUpdated = usePersistFn((lead: Lead) =>
      leadUpdated$.next(lead),
    );
    const value = useMemo<LeadCenterContextValue>(
      () => ({ leadUpdated$, emitLeadUpdated }),
      [leadUpdated$, emitLeadUpdated],
    );
    return (
      <LeadCenterContext.Provider value={value}>
        {children}
      </LeadCenterContext.Provider>
    );
  },
);
